/**
 * Risposte rapide con "/" nel composer di Instagram e Messenger: stessi modelli
 * (whatsapp_message_templates), stesso menu e stesse regole di WhatsApp
 * (quickReplies.ts). {{nome}} e {{azienda}} si compilano dal Lead/Cliente
 * collegato alla conversazione (useLinkedClientVars).
 */

import { useCallback, useEffect, useMemo, useState, type KeyboardEvent, type RefObject } from 'react';
import { useWhatsAppChatMetaStore } from '@/lib/stores/whatsappChatMetaStore';
import { QuickReplyMenu } from '@/components/whatsapp2/input/QuickReplyMenu';
import { useLinkedClientVars } from '@/components/whatsapp2/input/useLinkedClientVars';
import {
  detectSlashQuery, replaceSlashToken, filterTemplates, fillTemplateVariables, type TemplateLike,
} from '@/components/whatsapp2/input/quickReplies';

interface Options {
  organizationId: string;
  clientId: string | null;
  conversationId: string;
  text: string;
  setText: (value: string) => void;
  textareaRef: RefObject<HTMLTextAreaElement>;
}

export function useMetaQuickReplies({ organizationId, clientId, conversationId, text, setText, textareaRef }: Options) {
  const [slash, setSlash] = useState<{ query: string; start: number } | null>(null);
  const [index, setIndex] = useState(0);
  const templates = useWhatsAppChatMetaStore((s) => s.templates);
  const loading = useWhatsAppChatMetaStore((s) => s.templatesLoading);
  const fetchTemplates = useWhatsAppChatMetaStore((s) => s.fetchTemplates);
  const vars = useLinkedClientVars(clientId);

  useEffect(() => { setSlash(null); }, [conversationId]);

  // Modelli caricati solo alla prima "/" (come in WhatsApp).
  const open = slash !== null;
  useEffect(() => {
    if (open && templates.length === 0 && organizationId && !loading) void fetchTemplates(organizationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, organizationId]);

  const results = useMemo(
    () => (slash ? filterTemplates(templates as TemplateLike[], slash.query) : []),
    [slash, templates],
  );

  const pick = useCallback((t: TemplateLike) => {
    if (!slash) return;
    const el = textareaRef.current;
    const cursor = el?.selectionStart ?? text.length;
    const filled = fillTemplateVariables(t.content, vars as Record<string, string | undefined>);
    const r = replaceSlashToken(text, slash.start, cursor, filled);
    setSlash(null);
    setText(r.text);
    requestAnimationFrame(() => {
      const ta = textareaRef.current;
      if (ta) { ta.focus(); ta.setSelectionRange(r.cursor, r.cursor); }
    });
  }, [slash, text, vars, setText, textareaRef]);

  /** Da chiamare nell'onChange del campo. */
  const onTextChange = useCallback((value: string, cursor: number) => {
    const next = detectSlashQuery(value, cursor);
    setSlash(next);
    if (next) setIndex(0);
  }, []);

  /** Frecce, Invio/Tab ed Esc mentre il menu e' aperto. true = tasto gestito. */
  const onKeyDown = useCallback((e: KeyboardEvent<HTMLTextAreaElement>): boolean => {
    if (!slash) return false;
    if (e.key === 'Escape') { e.preventDefault(); setSlash(null); return true; }
    if (results.length === 0) return false;
    if (e.key === 'ArrowDown') { e.preventDefault(); setIndex((i) => (i + 1) % results.length); return true; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setIndex((i) => (i - 1 + results.length) % results.length); return true; }
    if ((e.key === 'Enter' && !e.shiftKey) || e.key === 'Tab') {
      e.preventDefault();
      pick(results[Math.min(index, results.length - 1)]);
      return true;
    }
    return false;
  }, [slash, results, index, pick]);

  const menu = slash ? (
    <QuickReplyMenu
      items={results}
      activeIndex={Math.min(index, Math.max(0, results.length - 1))}
      loading={loading}
      query={slash.query}
      onPick={pick}
      onHover={setIndex}
    />
  ) : null;

  return { menu, onTextChange, onKeyDown, close: () => setSlash(null) };
}
